import { useEffect } from 'react'
import { Flex, HStack, Spinner, VStack } from '@chakra-ui/react'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, useNavigate, useRouter } from '@tanstack/react-router'
import { motion } from 'framer-motion'
import { TriangleAlert } from 'lucide-react'

import { formatPageTitle } from '@/constants/stringVars'
import CustomButton from '@/components/atoms/CustomButton'
import { MAX_CONTAINER_WIDTH } from '@/constants/dimensions'
import ContentEmptyState from '@/modules/content/common/ContentEmptyState'
import ContentReader from '@/modules/content/feed/ContentReader'
import svgIcons from '@/assets/svg/icons'
import { getContentByIdOptions } from '@/utils/queryOptions'
import { text } from '@/theme/colors/palettes'

// The full read, reached from the loop's "Read Full Article" and from shared links.
//
// This is the ONLY standalone content page. Posts and videos render completely inside the
// loop, so they have no full page — a shared link to either opens the loop anchored at
// that item instead.
const ArticlePage = () => {
  const { contentId } = Route.useParams()
  const navigate = useNavigate()
  const router = useRouter()

  const {
    data: content,
    isLoading,
    isError,
    refetch,
  } = useQuery(getContentByIdOptions(contentId))

  // Prefer real history so returning to the loop restores its position and scroll. Only
  // fall back to the feed when this page was opened directly (a shared link).
  const goBack = () => {
    if (window.history.length > 1) router.history.back()
    else navigate({ to: '/explore' })
  }

  useEffect(() => {
    if (content?.title) document.title = formatPageTitle(content.title)
  }, [content?.title])

  return (
    <VStack
      width={MAX_CONTAINER_WIDTH}
      marginTop={'20px'}
      gap={'16px'}
      alignItems={'stretch'}
    >
      <HStack>
        <CustomButton
          isTransparent
          iconBeforeLabelSvgPath={svgIcons.leftArrow}
          iconBeforeLabelSvgBoxHeight={12}
          iconBeforeLabelSvgColor={text.mediumBlue}
          labelColor={'text.mediumBlue'}
          labelHoverColor={'extra.black'}
          label={'Back'}
          onClick={goBack}
        />
      </HStack>

      {isLoading ? (
        <HStack width={'100%'} justifyContent={'center'} paddingY={'80px'}>
          <Spinner color={'primary.500'} size={'lg'} />
        </HStack>
      ) : isError || !content ? (
        <ContentEmptyState
          icon={TriangleAlert}
          title="Couldn’t load this content"
          description="Something went wrong, or this content is no longer available."
          actionLabel="Try again"
          onAction={() => refetch()}
        />
      ) : (
        <Flex width={'100%'} justifyContent={'center'}>
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            style={{ width: '100%', display: 'flex', justifyContent: 'center' }}
          >
            <ContentReader content={content} />
          </motion.div>
        </Flex>
      )}
    </VStack>
  )
}

export const Route = createFileRoute(
  '/_authenticated/_explore/article/$contentId',
)({
  staticData: { title: 'Article' },
  loader: ({ context, params }) =>
    context.queryClient.ensureQueryData(
      getContentByIdOptions(params.contentId),
    ),
  component: ArticlePage,
})
